import { supabase } from "@/shared/lib/supabaseClient";

/**
 * Camada de dados do elo Visitantes (migration 0101).
 *
 * Regras que este service NUNCA reimplementa (moram inteiras no banco):
 * - Quem vê o quê: as 5 tabelas do elo não têm política de leitura — todo acesso
 *   passa por RPCs `security definer` que decidem pelo papel de quem chama
 *   (ministério responsável, líder/gestor de outro ministério, administrador).
 *   Para o líder, o banco já devolve a lista e a ficha reduzidas (sem criança,
 *   sem quem frequenta outra igreja, sem endereço/pedido de oração/responsável).
 * - Escrita: só RPCs; cada uma revalida papel, igreja, dados e versão
 *   (concorrência otimista — erro 40001 = a tela está velha).
 * - Notificações (sino + push): criadas dentro das próprias RPCs.
 *
 * As RPCs novas ainda não estão em `database.types.ts` (as tipagens são
 * regeneradas depois que a migration 0101 for aplicada), por isso a chamada
 * passa por `chamarRpc`, com os contratos escritos à mão neste arquivo.
 */

// ---------------------------------------------------------------- contratos

export interface VisitanteLista {
  id: string;
  nome: string;
  nascimento: string | null;
  /** Sempre mascarado na lista: "(••) •••••-1234". */
  telefone_mascarado: string | null;
  visitas: number;
  ultima_visita: string | null;
  /** Data de todas as visitas, da mais recente para a mais antiga (filtro de período). */
  datas_visitas: string[];
  contatos: number;
  ultimo_contato: string | null;
  /** Para o líder, só "nao" ou null. */
  preferencia_contato: string | null;
  sem_responsavel: boolean;
  /** Informações de líderes ainda não lidas (só o ministério responsável). */
  informacoes_novas: number;
}

export interface ContatoFicha {
  /** Só o ministério responsável recebe `id` e `por`. */
  id?: string;
  data: string;
  canal: string;
  observacao: string | null;
  por?: string | null;
}

export interface InformacaoFicha {
  id: string;
  texto: string;
  lida: boolean;
  em: string;
  de: string | null;
  ministerio: string | null;
}

/** Vínculo familiar (tabela `pessoa_parentescos`). Na ficha: o que o OUTRO é para esta pessoa. */
export type ParentescoChave =
  | "conjuge"
  | "filho"
  | "irmao"
  | "pai_mae"
  | "sobrinho"
  | "neto"
  | "amigo"
  | "outro"
  // Só aparecem na ficha (lado inverso de sobrinho/neto); nunca são enviados ao banco.
  | "tio"
  | "avo";

export interface FamiliarFicha {
  id: string;
  nome: string;
  idade: number | null;
  parentesco: ParentescoChave | null;
}

/** Registro de consentimento (só gestão/administrador recebem). */
export interface ConsentimentoFicha {
  em: string | null;
  por: string | null;
  retirado_em: string | null;
  retirado_por: string | null;
}

export interface VisitanteFicha {
  id: string;
  nome: string;
  nascimento: string | null;
  telefone: string | null;
  email: string | null;
  /** Opcional; chave de `ESTADOS_CIVIS`. */
  estado_civil?: string | null;
  /** "masculino" | "feminino"; `null` = não informado (cadastros anteriores ao campo). */
  sexo?: string | null;
  versao: number;
  preferencia_contato: string | null;
  observacao: string | null;
  /** Datas das visitas, da mais recente para a mais antiga. */
  visitas: string[];
  contatos_total: number;
  ultimo_contato: string | null;
  contatos: ContatoFicha[];
  cidade: string | null;
  /** "Veio com": quem foi cadastrado junto. O líder só recebe quem ele poderia abrir. */
  familia: FamiliarFicha[];
  // Abaixo: só o ministério responsável e o administrador recebem.
  consentimento?: ConsentimentoFicha;
  resp_nome?: string | null;
  resp_telefone?: string | null;
  igreja_situacao?: string | null;
  igreja_nome?: string | null;
  bairro?: string | null;
  endereco?: string | null;
  pedido_oracao?: string | null;
  informacoes?: InformacaoFicha[];
}

export type SituacaoIndicacao = "pendente" | "adicionada" | "descartada";

export interface Indicacao {
  id: string;
  nome: string;
  telefone: string | null;
  email: string | null;
  nascimento: string | null;
  data_visita: string;
  resp_nome: string | null;
  resp_telefone: string | null;
  igreja_situacao: string | null;
  igreja_nome: string | null;
  cidade: string | null;
  bairro: string | null;
  endereco: string | null;
  preferencia_contato: string | null;
  pedido_oracao: string | null;
  nota: string | null;
  estado_civil?: string | null;
  sexo?: string | null;
  situacao: SituacaoIndicacao;
  motivo: string | null;
  versao: number;
  created_at: string;
  avaliada_em: string | null;
  ministerio: string | null;
  ministerio_id: string | null;
  /** Só na visão do ministério responsável. */
  de?: string | null;
  editada?: boolean;
  /** Só em "Meus pendentes". */
  aviso_lido?: boolean;
  /** Familiares cadastrados junto (0106). Some das respostas de "Sobre a pessoa" depois da avaliação. */
  familiares?: FamiliarPendente[] | null;
  /** O líder registrou que avisou a pessoa e ela concordou (0106). */
  consentimento?: boolean;
}

/** Familiar guardado dentro do Visitante pendente (jsonb `familiares`). */
export interface FamiliarPendente {
  parentesco: Exclude<ParentescoChave, "tio" | "avo">;
  nome: string;
  nascimento: string;
  telefone?: string | null;
  igreja_situacao?: string | null;
  igreja_nome?: string | null;
  cidade?: string | null;
  bairro?: string | null;
  endereco?: string | null;
  preferencia_contato?: string | null;
  pedido_oracao?: string | null;
  estado_civil?: string | null;
  sexo?: string | null;
}

/** "Sobre a pessoa": respostas opcionais que só a liderança vê (VIS-DEC-034). */
export interface DadosSobre {
  igrejaSituacao: "" | "outra" | "sem";
  igrejaNome: string;
  cidade: string;
  bairro: string;
  endereco: string;
  preferenciaContato: "" | "visita" | "contato" | "nao";
  pedidoOracao: string;
}

/** Dados do formulário (cadastro, edição e informe). Texto vazio vira "não informado" no banco. */
export interface DadosVisitante extends DadosSobre {
  nome: string;
  telefone: string;
  email: string;
  /** Estado civil (opcional, só de quem tem 12 anos ou mais); "" = não informado. */
  estadoCivil: string;
  /** "masculino" | "feminino"; "" = não informado (obrigatório só em cadastro novo e informe). */
  sexo: string;
  nascimento: string;
  /** Só no cadastro novo e no informe. */
  dataVisita?: string;
  respNome: string;
  respTelefone: string;
  /** `observacao` (cadastro) ou `nota` (informe do líder). */
  observacao: string;
  /** Só no informe do líder: ministério de origem (opcional). */
  ministerioId?: string;
}

/** Familiar da etapa 2 do cadastro em família. */
export interface DadosFamiliar {
  parentesco: Exclude<ParentescoChave, "tio" | "avo">;
  nome: string;
  nascimento: string;
  /** Só para quem tem 12 anos ou mais. */
  telefone: string;
  /** Opcional; só de quem tem 12 anos ou mais. */
  estadoCivil?: string;
  /** "masculino" | "feminino" (de qualquer idade); "" = não informado. */
  sexo?: string;
  /** "Sobre a pessoa" — só de quem tem 12 anos ou mais (criança não guarda, VIS-DEC-034). */
  sobre?: DadosSobre;
}

export interface ResultadoFamilia {
  familia_id: string;
  ids: string[];
}

export interface MinisterioLider {
  id: string;
  nome: string;
}

// -------------------------------------------------------------------- infra

type RespostaRpc = { data: unknown; error: { code?: string; message?: string } | null };

function chamarRpc(nome: string, args?: Record<string, unknown>): PromiseLike<RespostaRpc> {
  const cliente = supabase as unknown as {
    rpc: (n: string, a?: Record<string, unknown>) => PromiseLike<RespostaRpc>;
  };
  return cliente.rpc(nome, args);
}

/**
 * As mensagens das RPCs são escritas em português para o usuário (P0001), então
 * passam como vieram; 42501 (sem permissão) vira texto fixo; o resto cai no fallback.
 */
function traduzirErro(error: { code?: string; message?: string } | null, fallback: string): Error {
  if (!error) return new Error(fallback);
  if (error.code === "42501")
    return new Error("Você não tem permissão para esta ação no elo Visitantes.");
  if ((error.code === "P0001" || error.code === "40001") && error.message)
    return new Error(error.message);
  return new Error(fallback);
}

async function executar<T>(
  nome: string,
  args: Record<string, unknown> | undefined,
  fallback: string,
): Promise<T> {
  const { data, error } = await chamarRpc(nome, args);
  if (error) throw traduzirErro(error, fallback);
  return data as T;
}

const vazioParaNulo = (v: string | undefined | null) => {
  const t = (v ?? "").trim();
  return t === "" ? null : t;
};

const soDigitos = (v: string | undefined | null) => {
  const d = (v ?? "").replace(/\D/g, "");
  return d === "" ? null : d;
};

/** Argumentos comuns às RPCs de cadastro/edição/informe (nomes `p_*` do banco). */
function argsDosDados(d: DadosVisitante): Record<string, unknown> {
  const pref = d.preferenciaContato || null;
  const querVisita = pref === "visita";
  const outra = d.igrejaSituacao === "outra";
  return {
    p_nome: d.nome.trim(),
    p_telefone: soDigitos(d.telefone),
    p_email: vazioParaNulo(d.email),
    p_estado_civil: vazioParaNulo(d.estadoCivil),
    // Em branco na edição não apaga o sexo já gravado (o banco usa coalesce).
    p_sexo: vazioParaNulo(d.sexo),
    p_nascimento: d.nascimento,
    p_resp_nome: vazioParaNulo(d.respNome),
    p_resp_telefone: soDigitos(d.respTelefone),
    p_igreja_situacao: d.igrejaSituacao || null,
    p_igreja_nome: outra ? vazioParaNulo(d.igrejaNome) : null,
    p_cidade: vazioParaNulo(d.cidade),
    // Endereço só é guardado para quem quer visita em casa (o banco recusa o resto).
    p_bairro: querVisita ? vazioParaNulo(d.bairro) : null,
    p_endereco: querVisita ? vazioParaNulo(d.endereco) : null,
    p_preferencia_contato: pref,
    p_pedido_oracao: vazioParaNulo(d.pedidoOracao),
  };
}

/** Principal da família (etapa 1) no formato do jsonb `p_principal`. */
function principalParaJson(p: DadosVisitante): Record<string, unknown> {
  const visita = p.preferenciaContato === "visita";
  return {
    nome: p.nome.trim(),
    telefone: soDigitos(p.telefone),
    email: vazioParaNulo(p.email),
    estado_civil: vazioParaNulo(p.estadoCivil),
    sexo: vazioParaNulo(p.sexo),
    nascimento: p.nascimento,
    igreja_situacao: p.igrejaSituacao || null,
    igreja_nome: p.igrejaSituacao === "outra" ? vazioParaNulo(p.igrejaNome) : null,
    cidade: vazioParaNulo(p.cidade),
    bairro: visita ? vazioParaNulo(p.bairro) : null,
    endereco: visita ? vazioParaNulo(p.endereco) : null,
    preferencia_contato: p.preferenciaContato || null,
    pedido_oracao: vazioParaNulo(p.pedidoOracao),
    observacao: vazioParaNulo(p.observacao),
  };
}

/** Familiar da etapa 2 no formato do jsonb `p_membros` / `p_familiares`. */
function membroParaJson(m: DadosFamiliar): Record<string, unknown> {
  const s = m.sobre;
  const visita = s?.preferenciaContato === "visita";
  return {
    parentesco: m.parentesco,
    nome: m.nome.trim(),
    nascimento: m.nascimento,
    telefone: soDigitos(m.telefone),
    estado_civil: vazioParaNulo(m.estadoCivil),
    sexo: vazioParaNulo(m.sexo),
    igreja_situacao: s?.igrejaSituacao || null,
    igreja_nome: s?.igrejaSituacao === "outra" ? vazioParaNulo(s.igrejaNome) : null,
    cidade: vazioParaNulo(s?.cidade),
    bairro: visita ? vazioParaNulo(s?.bairro) : null,
    endereco: visita ? vazioParaNulo(s?.endereco) : null,
    preferencia_contato: s?.preferenciaContato || null,
    pedido_oracao: vazioParaNulo(s?.pedidoOracao),
  };
}

// ------------------------------------------------------------------ service

export const visitantesService = {
  listar: () =>
    executar<VisitanteLista[]>(
      "visitantes_listar",
      undefined,
      "Não foi possível carregar os visitantes.",
    ),

  ficha: (id: string) =>
    executar<VisitanteFicha>("visitante_ficha", { p_id: id }, "Não foi possível abrir a ficha."),

  indicacoes: () =>
    executar<Indicacao[]>(
      "indicacoes_listar",
      undefined,
      "Não foi possível carregar os visitantes pendentes.",
    ),

  minhasIndicacoes: () =>
    executar<Indicacao[]>(
      "minhas_indicacoes",
      undefined,
      "Não foi possível carregar o que você cadastrou.",
    ),

  /** `consentimento` = "avisei a pessoa e ela concordou" (obrigatório marcar no cadastro novo). */
  criar: (d: DadosVisitante, consentimento: boolean) =>
    executar<string>(
      "visitante_criar",
      {
        ...argsDosDados(d),
        p_data_visita: d.dataVisita,
        p_observacao: vazioParaNulo(d.observacao),
        p_consentimento: consentimento,
      },
      "Não foi possível cadastrar o visitante.",
    ),

  /** Cadastro em família: a pessoa adulta (etapa 1) e de 1 a 10 familiares (etapa 2), de uma vez. */
  criarFamilia: (principal: DadosVisitante, membros: DadosFamiliar[], consentimento: boolean) =>
    executar<ResultadoFamilia>(
      "visitante_criar_familia",
      {
        p_principal: principalParaJson(principal),
        p_data_visita: principal.dataVisita,
        p_membros: membros.map(membroParaJson),
        p_consentimento: consentimento,
      },
      "Não foi possível cadastrar a família.",
    ),

  /** Registra o consentimento de um cadastro antigo. Devolve a nova versão. */
  registrarConsentimento: (id: string, versao: number) =>
    executar<number>(
      "visitante_registrar_consentimento",
      { p_id: id, p_versao: versao },
      "Não foi possível registrar o consentimento.",
    ),

  /** Retira o consentimento: apaga só "Sobre a pessoa" e mantém nome, contato e visitas. */
  retirarConsentimento: (id: string, versao: number) =>
    executar<number>(
      "visitante_retirar_consentimento",
      { p_id: id, p_versao: versao },
      "Não foi possível retirar o consentimento.",
    ),

  /** Devolve a nova versão do cadastro. */
  editar: (id: string, versao: number, d: DadosVisitante) =>
    executar<number>(
      "visitante_editar",
      { p_id: id, p_versao: versao, ...argsDosDados(d), p_observacao: vazioParaNulo(d.observacao) },
      "Não foi possível salvar o cadastro.",
    ),

  registrarVisita: (id: string, data: string) =>
    executar<void>(
      "visitante_registrar_visita",
      { p_id: id, p_data: data },
      "Não foi possível registrar a visita.",
    ),

  registrarContato: (id: string, data: string, canal: string, observacao: string) =>
    executar<void>(
      "visitante_registrar_contato",
      { p_id: id, p_data: data, p_canal: canal, p_observacao: vazioParaNulo(observacao) },
      "Não foi possível registrar o contato.",
    ),

  excluir: (id: string, versao: number) =>
    executar<void>(
      "visitante_excluir",
      { p_id: id, p_versao: versao },
      "Não foi possível excluir o visitante.",
    ),

  marcarInformacoesLidas: (id: string) =>
    executar<void>(
      "visitante_informacoes_marcar_lidas",
      { p_id: id },
      "Não foi possível marcar como lidas.",
    ),

  passarInformacao: (pessoaId: string, texto: string, ministerioId?: string) =>
    executar<void>(
      "visitante_passar_informacao",
      { p_pessoa_id: pessoaId, p_texto: texto.trim(), p_ministerio_id: ministerioId || null },
      "Não foi possível passar a informação.",
    ),

  /**
   * Cadastra um Visitante pendente (o ministério responsável avalia). Mesmo formulário completo do
   * cadastro: família opcional e consentimento obrigatório (0106).
   */
  informar: (d: DadosVisitante, familiares: DadosFamiliar[], consentimento: boolean) =>
    executar<string>(
      "indicacao_criar",
      {
        ...argsDosDados(d),
        p_data_visita: d.dataVisita,
        p_ministerio_id: d.ministerioId || null,
        p_nota: vazioParaNulo(d.observacao),
        p_familiares: familiares.length > 0 ? familiares.map(membroParaJson) : null,
        p_consentimento: consentimento,
      },
      "Não foi possível cadastrar o visitante pendente.",
    ),

  editarInformado: (id: string, versao: number, d: DadosVisitante, familiares: DadosFamiliar[]) =>
    executar<number>(
      "indicacao_editar",
      {
        p_id: id,
        p_versao: versao,
        ...argsDosDados(d),
        p_data_visita: d.dataVisita,
        p_ministerio_id: d.ministerioId || null,
        p_nota: vazioParaNulo(d.observacao),
        p_familiares: familiares.length > 0 ? familiares.map(membroParaJson) : null,
      },
      "Não foi possível salvar a informação.",
    ),

  marcarAvisoLido: (id: string) =>
    executar<void>(
      "indicacao_marcar_aviso_lido",
      { p_id: id },
      "Não foi possível marcar o aviso como lido.",
    ),

  adicionarInformado: (id: string, versao: number, d: DadosVisitante) =>
    executar<string>(
      "indicacao_adicionar",
      {
        p_id: id,
        p_versao: versao,
        ...argsDosDados(d),
        p_data_visita: d.dataVisita,
        p_observacao: vazioParaNulo(d.observacao),
      },
      "Não foi possível adicionar o visitante.",
    ),

  descartarInformado: (id: string, versao: number, motivo: string) =>
    executar<void>(
      "indicacao_descartar",
      { p_id: id, p_versao: versao, p_motivo: vazioParaNulo(motivo) },
      "Não foi possível concluir.",
    ),

  registrarVisitaDoInformado: (id: string, versao: number, visitanteId: string) =>
    executar<void>(
      "indicacao_registrar_visita_existente",
      { p_id: id, p_versao: versao, p_visitante_id: visitanteId },
      "Não foi possível registrar a visita.",
    ),

  /**
   * Ministérios que a pessoa lidera/gerencia (para "de qual ministério vem a informação").
   * É só uma conveniência de tela: o banco revalida (`lidera_ou_gerencia_ministerio`).
   */
  async ministeriosQueLidero(pessoaId: string): Promise<MinisterioLider[]> {
    const { data, error } = await supabase
      .from("pessoa_ministerio")
      .select(
        "ministerio_id, ministerios(id, nome), funcoes_ministeriais(eh_lider, pode_gerenciar_modulo)",
      )
      .eq("pessoa_id", pessoaId)
      .eq("status", "ativo");
    if (error) throw new Error("Não foi possível carregar seus ministérios.");
    const mapa = new Map<string, MinisterioLider>();
    for (const linha of data ?? []) {
      const funcao = Array.isArray(linha.funcoes_ministeriais)
        ? linha.funcoes_ministeriais[0]
        : linha.funcoes_ministeriais;
      const ministerio = Array.isArray(linha.ministerios)
        ? linha.ministerios[0]
        : linha.ministerios;
      if (ministerio && (funcao?.eh_lider || funcao?.pode_gerenciar_modulo)) {
        mapa.set(ministerio.id, { id: ministerio.id, nome: ministerio.nome });
      }
    }
    return [...mapa.values()].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  },

  /** Nome(s) do ministério responsável pelo elo — para o selo "Ministério responsável". Falha = sem selo. */
  async ministeriosResponsaveis(): Promise<string[]> {
    const { data, error } = await supabase
      .from("igreja_modulos_ministerios")
      .select("ministerios(nome)")
      .eq("modulo_codigo", "visitantes");
    if (error) return [];
    const nomes = (data ?? [])
      .map((l) => (Array.isArray(l.ministerios) ? l.ministerios[0]?.nome : l.ministerios?.nome))
      .filter((n): n is string => !!n);
    return [...new Set(nomes)].sort((a, b) => a.localeCompare(b, "pt-BR"));
  },
};
